import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';

// Language configurations. Only en + he ship translations; the 14 partial
// English-stub locales were removed (A-32) rather than left half-populated.
export const LANGUAGES = {
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    dir: 'ltr',
    flag: '\u{1F1FA}\u{1F1F8}'
  },
  he: {
    code: 'he',
    name: 'Hebrew',
    nativeName: '\u05E2\u05D1\u05E8\u05D9\u05EA',
    dir: 'rtl',
    flag: '\u{1F1EE}\u{1F1F1}'
  }
} as const;

export type LanguageCode = keyof typeof LANGUAGES;

// All 26 namespace keys (was 24 — added `contracts` so the
// /contracts page can localize, and `messages` is reserved for a
// follow-up extraction pass).
const NAMESPACES = [
  'common', 'auth', 'dashboard', 'hotels', 'profile', 'settings',
  'analytics', 'affiliates', 'errors', 'validation', 'reports',
  'commissions', 'payouts', 'materials', 'notifications',
  'toasts', 'onboarding', 'admin', 'onboardingManagement',
  'bookings', 'cookiePolicy', 'partnerships', 'verification',
  'contracts', 'discovery', 'ugc', 'messaging', 'promotions',
  'segments', 'marketplace'
] as const;

// Lazy locale loading: each JSON becomes its own Vite chunk and is fetched
// on demand when i18next asks for (language, namespace). Replaces the
// previous approach that statically imported 384 JSONs (16 langs × 24 ns)
// into the main bundle — see issue #312.
const localeModules = import.meta.glob('../locales/*/*.json');

const backend = resourcesToBackend((lng: string, ns: string) => {
  const path = `../locales/${lng}/${ns}.json`;
  const loader = localeModules[path] as (() => Promise<{ default: unknown }>) | undefined;
  if (!loader) {
    return Promise.reject(new Error(`No translations for ${lng}/${ns}`));
  }
  return loader().then((m) => m.default);
});

// Initialize i18next
i18n
  .use(backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Don't set lng here - let LanguageDetector handle it from localStorage
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: [...NAMESPACES],
    supportedLngs: Object.keys(LANGUAGES),
    // Mark languages as partially bundled so i18next fetches only the
    // namespaces actually used, not everything upfront.
    partialBundledLanguages: true,
    load: 'currentOnly',

    interpolation: {
      escapeValue: false // React already safes from xss
    },

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng'
    },

    react: {
      useSuspense: false
    }
  });

// Update document direction based on language
i18n.on('languageChanged', (lng: string) => {
  const language = LANGUAGES[lng as LanguageCode];
  if (language) {
    document.documentElement.dir = language.dir;
    document.documentElement.lang = lng;
  }
});

// Set initial direction
const currentLang = i18n.language as LanguageCode;
if (LANGUAGES[currentLang]) {
  document.documentElement.dir = LANGUAGES[currentLang].dir;
  document.documentElement.lang = currentLang;
}

export default i18n;
